import { DeleteOutlined, FileSyncOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Tag } from 'antd';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { currentAdministratorSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import RoleEnum from '~Api/Administrator/RoleEnum';
import IApplication from '~Api/Application/IApplication';
import IBorrower from '~Api/Application/IBorrower';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import BankruptcyCheck from '~Applications/Application/BankruptcyCheck';
import CompanyTradingHistory from '~Applications/Application/CompanyTradingHistory';
import CreditCheck from '~Applications/Application/CreditCheck';
import CreditMemo from '~Applications/Application/CreditMemo';
import InsuranceExpiryDate from '~Applications/Application/InsuranceExpiryDate';
import ValuationInspectionDate from '~Applications/Application/ValuationInspectionDate';
import { IGlobalState } from '~reducer';
import UploadConditionDocument from './UploadConditionDocument';
import UploadedConditionDocumentList from './UploadedConditionDocumentList';
import { IDictionary } from '~utilities/IDictionary';
import { ColumnType } from 'antd/lib/table';
import {
    applicationConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationRfiItemsSelector,
} from '~Applications/selectors';
import {
    applicationConditionDeleteAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationRfiItemDeleteAction,
    applicationRfiItemRequestedAction,
    applicationRfiItemSuppliedAction,
    applicationRfiItemsAddAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import ConditionDocumentApprovalStatusEnum from '~Api/Application/ConditionDocumentApprovalStatusEnum';
import IRfiItem from '~Api/Application/IRfiItem';
import RfiWorkflowStatusEnum from '~Api/Application/RfiWorkflowStatusEnum';
import { aggregatorsListAction } from '~Aggregators/actions';
import { aggregatorsSelector } from '~Aggregators/selectors';
import IAggregator from '~Api/Aggregator/IAggregator';
import TitleSearch from '~Applications/Application/TitleSearch';
import { workflowStatusLabels } from '~Applications/workflowStatusLabels';
import ModalStateEnum from '~UI/ModalStateEnum';
import RequiredBeforeModal from './RequiredBeforeModal';
import Link from 'antd/lib/typography/Link';
import { ColumnFilterItem } from 'antd/lib/table/interface';

const nextWorkflowStatuses: IDictionary<WorkflowStatusEnum> = {
    [WorkflowStatusEnum.Draft]: WorkflowStatusEnum.New,
    [WorkflowStatusEnum.New]: WorkflowStatusEnum.ConditionalApproval,
    [WorkflowStatusEnum.ConditionalApproval]: WorkflowStatusEnum.Underwriting,
    [WorkflowStatusEnum.Underwriting]: WorkflowStatusEnum.FormalApproval,
    [WorkflowStatusEnum.FormalApproval]: WorkflowStatusEnum.LegalDocuments,
    [WorkflowStatusEnum.LegalDocuments]: WorkflowStatusEnum.Settlement,
    [WorkflowStatusEnum.Settlement]: WorkflowStatusEnum.Warehoused,
};

enum WorkflowStatusNoneEnum {
    None = 'NONE',
}

type WorkflowStatusWithNoneEnum = WorkflowStatusEnum | WorkflowStatusNoneEnum;

const conditionUrls: IDictionary<string> = {
    [ConditionTypeEnum.AssetLiabilityStatement]: 'https://www.funding.com.au/assets-liabilities-statement/',
    [ConditionTypeEnum.BankSolicitorAccountantDetails]: 'https://www.funding.com.au/bank-solicitor-accountant-details/',
    [ConditionTypeEnum.BusinessPurposeDeclaration]: 'https://www.funding.com.au/business-purpose-declaration/',
    [ConditionTypeEnum.CompletedLoanApplication]: 'https://www.funding.com.au/loan-application/',
    [ConditionTypeEnum.ConsumerPurposeDeclaration]: 'https://www.funding.com.au/consumer-purpose-declaration/',
    [ConditionTypeEnum.LoanPurposeDeclaration]: 'https://www.funding.com.au/loan-purpose-declaration/',
    [ConditionTypeEnum.MonthlyBudget]: 'https://www.funding.com.au/monthly-budget/',
    [ConditionTypeEnum.PrivacyConsentForm]: 'https://www.funding.com.au/privacy-consent-form/',
    [ConditionTypeEnum.RecordOfInterview]: 'https://www.funding.com.au/record-of-interview/',
};

const conditionComponents: IDictionary<object> = {
    [ConditionTypeEnum.BankruptcyRegisterSearch]: BankruptcyCheck,
    [ConditionTypeEnum.BuildingReplacementInsurance]: InsuranceExpiryDate,
    [ConditionTypeEnum.CompanyInDepthTradingHistory]: CompanyTradingHistory,
    [ConditionTypeEnum.CreditFileSearchReport]: CreditCheck,
    [ConditionTypeEnum.CurrentTitleSearch]: TitleSearch,
    [ConditionTypeEnum.Memo]: CreditMemo,
    [ConditionTypeEnum.Valuation]: ValuationInspectionDate,
};

const statuses: WorkflowStatusEnum[] = [
    WorkflowStatusEnum.Draft,
    WorkflowStatusEnum.New,
    WorkflowStatusEnum.ConditionalApproval,
    WorkflowStatusEnum.Underwriting,
    WorkflowStatusEnum.FormalApproval,
    WorkflowStatusEnum.LegalDocuments,
    WorkflowStatusEnum.Settlement,
    WorkflowStatusEnum.Warehoused,
];

const rfiWorkflowStatusClassNames: IDictionary<string> = {
    [RfiWorkflowStatusEnum.Requested]: 'requested',
    [RfiWorkflowStatusEnum.Supplied]: 'supplied',
};

interface IConditionTypeDocument {
    conditionType?: ConditionTypeEnum;
    conditionUuid?: string;
    name: string;
    requiredWorkflowStatuses: WorkflowStatusEnum[];
    typedConditionDocuments?: IConditionDocument[];
    uid: string;
}

export default function ConditionDocumentsList(props: {
    application: IApplication;
    applicationBorrower?: IBorrower;
    applicationProperty?: IApplicationProperty;
}): ReactElement {
    const {
        application,
        applicationBorrower,
        applicationProperty,
    } = props;
    const [ requiredBeforeConditionUuid, setRequiredBeforeConditionUuid ] = useState<string>(null);
    const [ requiredBeforeModalState, setRequiredBeforeModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState): IDictionary<IDictionary<ICondition>> => applicationEntityConditionsSelector(state, application.uuid));
    const currentAdministrator: IAdministrator = useSelector(currentAdministratorSelector);
    const conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]> = useSelector((state: IGlobalState): IDictionary<IConditionDocument[]> => applicationConditionDocumentsByConditionUuidSelector(state, application.uuid));
    const rfiItems: IDictionary<IRfiItem> = useSelector((state: IGlobalState): IDictionary<IRfiItem> => applicationRfiItemsSelector(state, application.uuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, [
        aggregators,
        dispatch,
    ]);

    useEffect((): void => {
        if (!conditionDocumentsByConditionUuid) {
            dispatch(applicationConditionDocumentsListAction(application.uuid));
        }
    }, [
        application.uuid,
        conditionDocumentsByConditionUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(application.uuid));
        }
    }, [
        application.uuid,
        applicationEntityConditions,
        dispatch,
    ]);

    useEffect((): void => {
        if (!rfiItems) {
            dispatch(applicationRfiItemsListAction(application.uuid));
        }
    }, [
        application.uuid,
        dispatch,
        rfiItems,
    ]);

    if (!applicationEntityConditions || !aggregators || !conditionDocumentsByConditionUuid || !currentAdministrator || !rfiItems) {
        return (
            <Spin/>
        );
    }

    const entityUuid: string = (applicationBorrower && applicationBorrower.uuid) || (applicationProperty && applicationProperty.uuid) || application.uuid;
    const entityConditions: IDictionary<ICondition> = applicationEntityConditions[entityUuid] || {};
    const nextWorkflowStatus: WorkflowStatusEnum = nextWorkflowStatuses[application.workflowStatus] || null;

    const futureStatuses: WorkflowStatusEnum[] = [];
    const previousStatuses: WorkflowStatusEnum[] = [];
    let found: boolean = false;

    _.each(statuses, (status: WorkflowStatusEnum): void => {
        if (status === nextWorkflowStatus) {
            found = true;
        }

        if (found) {
            futureStatuses.push(status);
        } else {
            previousStatuses.push(status);
        }
    });

    const sortedStatuses: WorkflowStatusWithNoneEnum[] = [
        ...futureStatuses,
        WorkflowStatusNoneEnum.None,
        ...previousStatuses,
    ];

    const sortOrderByWorkflowStatus: IDictionary<number> = {};
    _.each(sortedStatuses, (workflowStatus: WorkflowStatusWithNoneEnum, index: number): void => {
        sortOrderByWorkflowStatus[workflowStatus] = index;
    });

    const conditionTypeDocuments: IConditionTypeDocument[] = _.map(entityConditions, (condition: ICondition): IConditionTypeDocument => {
        return {
            conditionType: condition.conditionType,
            conditionUuid: condition.uuid,
            name: condition.name,
            requiredWorkflowStatuses: condition.requiredWorkflowStatuses,
            typedConditionDocuments: conditionDocumentsByConditionUuid[condition.uuid] ? conditionDocumentsByConditionUuid[condition.uuid] : [],
            uid: condition.uuid,
        };
    });

    const columns: ColumnType<IConditionTypeDocument>[] = [
        {
            dataIndex: 'typedConditionDocuments',
            render: (typedConditionDocuments: IConditionDocument[], conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                if (_.some(typedConditionDocuments, (typedConditionDocument: IConditionDocument): boolean => [
                    ConditionDocumentApprovalStatusEnum.Legacy,
                    ConditionDocumentApprovalStatusEnum.Approved,
                ].includes(typedConditionDocument.approvalStatus))) {
                    return;
                }

                if (conditionTypeDocument.requiredWorkflowStatuses.includes(nextWorkflowStatus)) {
                    return <WarningOutlined/>;
                }

                return <QuestionCircleOutlined/>;
            },
            title: '',
            width: 50,
        },
        {
            render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ConditionComponent: any = conditionComponents[conditionTypeDocument.conditionType];
                const conditionComponentBlock: JSX.Element = ConditionComponent && (
                    <ConditionComponent
                        applicationUuid={application.uuid}
                        applicationBorrowerUuid={applicationBorrower && applicationBorrower.uuid}
                        applicationPropertyUuid={applicationProperty && applicationProperty.uuid}
                    />
                );

                return (
                    <Space>
                        <span>{conditionTypeDocument.name}</span>
                        {conditionUrls[conditionTypeDocument.conditionType] && <a href={conditionUrls[conditionTypeDocument.conditionType]} target='_blank' rel='noreferrer'><FaRegFilePdf/></a>}
                        {conditionComponentBlock}
                    </Space>
                );
            },
            title: 'Condition',
        },
        {
            render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => (
                <UploadedConditionDocumentList
                    application={application}
                    applicationBorrower={applicationBorrower}
                    applicationProperty={applicationProperty}
                    conditionUuid={conditionTypeDocument.conditionUuid}
                    conditionType={conditionTypeDocument.conditionType}
                />
            ),
            title: 'Documents',
            width: 460,
        },
        {
            render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                const rfiItem: IRfiItem = _.find(rfiItems, (rfiItem: IRfiItem): boolean => {
                    return (
                        rfiItem.conditionType
                        ? rfiItem.conditionType === conditionTypeDocument.conditionType
                        : rfiItem.applicationConditionUuid === conditionTypeDocument.conditionUuid
                    );
                });

                const isOwnerOrSenior: boolean = application.administratorUuid === currentAdministrator.uuid || RoleEnum.SeniorCreditManager === currentAdministrator.role;

                const rfiMenu: MenuProps = {
                    className: 'rfi-item-dropdown',
                    items: [
                        ...(!rfiItem ? [
                            {
                                key: 'request',
                                label: 'Request',
                                onClick: (): void => {
                                    dispatch(applicationRfiItemsAddAction(
                                        application.uuid,
                                        applicationBorrower && applicationBorrower.uuid,
                                        conditionTypeDocument.conditionUuid,
                                        applicationProperty && applicationProperty.uuid,
                                        conditionTypeDocument.conditionType,
                                    ));
                                },
                            },
                        ] : []),
                        ...(rfiItem && rfiItem.workflowStatus !== RfiWorkflowStatusEnum.Requested ? [
                            {
                                key: 'requested',
                                label: 'Requested',
                                onClick: (): void => {
                                    dispatch(applicationRfiItemRequestedAction(rfiItem.uuid));
                                },
                            },
                        ] : []),
                        ...(isOwnerOrSenior && rfiItem && rfiItem.workflowStatus !== RfiWorkflowStatusEnum.Supplied ? [
                            {
                                key: 'supplied',
                                label: 'Supplied',
                                onClick: (): void => {
                                    dispatch(applicationRfiItemSuppliedAction(rfiItem.uuid));
                                },
                            },
                        ] : []),
                        ...(isOwnerOrSenior && rfiItem ? [
                            {
                                danger: true,
                                key: 'cancel-request',
                                label: 'Cancel Request',
                                onClick: (): void => {
                                    dispatch(applicationRfiItemDeleteAction(application.uuid, rfiItem.uuid));
                                },
                            },
                        ] : []),
                    ],
                };

                const rfiBlock: JSX.Element = (isOwnerOrSenior || RoleEnum.CreditManager === currentAdministrator.role) && (
                    <Dropdown menu={rfiMenu}>
                        <Button className={`rfi-item ${rfiItem && rfiWorkflowStatusClassNames[rfiItem.workflowStatus]}`} type='link'>
                            <FileSyncOutlined/>
                        </Button>
                    </Dropdown>
                );

                const onClickDeleteCondition: () => void = (): void => {
                    Modal.confirm({
                        content: 'Are you sure you want to delete this condition?',
                        okText: 'Delete',
                        okType: 'danger',
                        onOk: (): void => {
                            const applicationPropertyUuid: string = applicationProperty ? applicationProperty.uuid : null;
                            const applicationBorrowerUuid: string = applicationBorrower ? applicationBorrower.uuid : null;
                            dispatch(applicationConditionDeleteAction(application.uuid, applicationBorrowerUuid, applicationPropertyUuid, conditionTypeDocument.conditionUuid));
                        },
                        title: 'Delete Condition',
                    });
                };

                return (
                    <Space>
                        <UploadConditionDocument
                            application={application}
                            applicationBorrower={applicationBorrower}
                            applicationProperty={applicationProperty}
                            conditionUuid={conditionTypeDocument.conditionUuid}
                            conditionType={conditionTypeDocument.conditionType}
                        />
                        {rfiBlock}
                        <Button
                            danger={true}
                            type='link'
                            onClick={onClickDeleteCondition}
                        >
                            <DeleteOutlined/>
                        </Button>
                    </Space>
                );
            },
            title: 'Actions',
            width: 132,
        },
        {
            filters: _.map(sortedStatuses, (workflowStatus: WorkflowStatusWithNoneEnum): ColumnFilterItem => ({
                text: workflowStatus !== WorkflowStatusNoneEnum.None ? workflowStatusLabels[workflowStatus] : 'None',
                value: workflowStatus,
            })),
            onFilter: (value: boolean | number | string, conditionTypeDocument: IConditionTypeDocument): boolean => conditionTypeDocument.requiredWorkflowStatuses.includes(value as WorkflowStatusEnum),
            render: (conditionTypeDocument: IConditionTypeDocument): ReactNode => {
                const condition: ICondition = applicationEntityConditions[entityUuid][conditionTypeDocument.conditionUuid];

                const onClickRequiredBefore: () => void = (): void => {
                    setRequiredBeforeConditionUuid(condition.uuid);
                    setRequiredBeforeModalState(ModalStateEnum.Open);
                };
                const onCancelRequiredBefore: () => void = (): void => {
                    setRequiredBeforeModalState(ModalStateEnum.Closed);
                };

                const requiredBeforeModalBlock: ReactElement = condition.uuid === requiredBeforeConditionUuid && (
                    <RequiredBeforeModal
                        condition={condition}
                        isOpen={requiredBeforeModalState === ModalStateEnum.Open}
                        onCancel={onCancelRequiredBefore}
                    />
                );

                const requiredWorkflowStatusLabel: string = (
                    condition.requiredWorkflowStatuses.length > 0
                    ? workflowStatusLabels[condition.requiredWorkflowStatuses[0]]
                    : 'None'
                );

                return (
                    <>
                        <Link className='required-before-tag' onClick={onClickRequiredBefore}>
                            <Tag>
                                { requiredWorkflowStatusLabel }
                            </Tag>
                        </Link>
                        {requiredBeforeModalBlock}
                    </>
                );
            },
            title: 'Required Before',
            width: 184,
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={_.sortBy(conditionTypeDocuments, [ 'name' ])}
            pagination={false}
            rowKey='uid'
        />
    );
}
